import Microgame from '../engine/Microgame.js';

export default class NeedleGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Aiguille !`, en: `Needle!`, es: `¡Aguja!` },
            controls: { fr: `Appuie quand le fil est face au chas de l'aiguille`, en: `Tap when the thread aligns with the eye of the needle`, es: `Toca cuando el hilo esté alineado con el ojo de la aguja` },
            objective: { fr: `Passe le fil dans le trou sans trembler`, en: `Pass the thread through the hole without shaking`, es: `Pasa el hilo por el agujero sin temblar` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="m14 10-6 6"/><path d="m10 14 4-4a2.83 2.83 0 0 1 4 4l-4 4"/><path d="M22 2 12 12l-2-2L2 22"/></svg>`,
            actions: [
                { type: 'wait', duration: 620 },
                { type: 'tap', x: 50, y: 18, duration: 280 },
                { type: 'wait', duration: 700 },
            ],
            loopDuration: 2400
        };
    }
    constructor(engine) {
        super(engine);
        this.thread = null;
        this.needleGroup = null;
        this.threadX = 0;
        this.direction = this.isPlayback ? 1 : (Math.random() > 0.5 ? 1 : -1);
        this.isStopped = false;
        
        // Difficulty scaling
        this.speedMult = engine.speedMultiplier || 1.0;
        // Damped speed: 1.0 -> 80, 4.0 -> ~160 (much slower start)
        const adjustedSpeedScale = 1 + (this.speedMult - 1) * 0.35;
        this.speed = 80 * adjustedSpeedScale;
        
        // Duration: 2.5s at x1 (halved per user request)
        this.duration = 2500 * (this.speedMult / adjustedSpeedScale);
        
        // Randomize needle Y position (25% to 75%)
        this.needleY = this.isPlayback ? 44 : (25 + Math.random() * 50);
    }

    setup() {
        this.engine.ui.instruction.textContent = "TAP TO THREAD!";

        // Target Zone (Horizontal)
        const targetZone = document.createElement('div');
        targetZone.style.position = 'absolute';
        targetZone.style.width = '20%';
        targetZone.style.height = '100%';
        targetZone.style.left = '50%';
        targetZone.style.top = '0';
        targetZone.style.transform = 'translateX(-50%)';
        targetZone.style.background = this.secondaryColor;
        targetZone.style.opacity = '0.1';
        targetZone.style.pointerEvents = 'none';
        this.container.appendChild(targetZone);

        // Needle Group (Front)
        this.needleGroup = document.createElement('div');
        this.needleGroup.style.position = 'absolute';
        this.needleGroup.style.width = '20px';
        this.needleGroup.style.height = '200px';
        this.needleGroup.style.top = `${this.needleY}%`;
        this.needleGroup.style.left = '50%';
        this.needleGroup.style.transform = 'translate(-50%, -50%)';
        this.needleGroup.style.zIndex = '10';
        this.container.appendChild(this.needleGroup);

        // Needle Top (Solid)
        const needleTop = document.createElement('div');
        needleTop.style.position = 'absolute';
        needleTop.style.width = '100%';
        needleTop.style.height = '20px';
        needleTop.style.background = this.primaryColor;
        needleTop.style.top = '0';
        needleTop.style.borderRadius = '10px 10px 0 0';
        needleTop.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.needleGroup.appendChild(needleTop);

        // Needle Eye (Wait for Thread)
        const needleEye = document.createElement('div');
        needleEye.style.position = 'absolute';
        needleEye.style.width = '100%';
        needleEye.style.height = '50px';
        needleEye.style.top = '20px';
        needleEye.style.border = `4px solid ${this.primaryColor}`;
        needleEye.style.boxSizing = 'border-box';
        this.needleGroup.appendChild(needleEye);

        // Needle Bottom (Pointy)
        const needleBottom = document.createElement('div');
        needleBottom.style.position = 'absolute';
        needleBottom.style.width = '100%';
        needleBottom.style.height = '130px';
        needleBottom.style.background = this.primaryColor;
        needleBottom.style.bottom = '0';
        needleBottom.style.borderRadius = '0 0 10px 10px';
        needleBottom.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.needleGroup.appendChild(needleBottom);

        // Thread (Always from Top)
        this.thread = document.createElement('div');
        this.thread.style.position = 'absolute';
        this.thread.style.width = '8px';
        this.thread.style.top = '0';
        // Eye center is at -55px relative to NeedleGroup center
        this.thread.style.height = `calc(${this.needleY}% - 55px)`;
        this.thread.style.background = this.secondaryColor;
        this.thread.style.left = '0%';
        this.thread.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.thread.style.zIndex = '5';
        this.container.appendChild(this.thread);

        // Initial Position (Randomized start side)
        this.threadX = this.isPlayback ? 10 : (Math.random() > 0.5 ? 10 : 90);
        this.updateThreadPos();

        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);
    }

    update(dt) {
        if (this._isVictoryAnimating) {
            this.engine.timer = this._frozenTimerValue;
            return;
        }
        if (!this.active || this.won || this.isStopped) return;

        this.threadX += this.speed * this.direction * (dt / 1000);

        if (this.threadX > 90) {
            this.threadX = 90;
            this.direction = -1;
        } else if (this.threadX < 10) {
            this.threadX = 10;
            this.direction = 1;
        }

        this.updateThreadPos();
    }

    updateThreadPos() {
        this.thread.style.left = `${this.threadX}%`;
        this.thread.style.transform = `translateX(-50%) rotate(${this.direction * 5}deg)`;
    }

    onTap() {
        if (!this.active || this.won || this.isStopped) return;
        this.isStopped = true;

        // Alignment check (Center is 50%)
        if (Math.abs(this.threadX - 50) <= 10) {
            // Success Sequence
            this._isVictoryAnimating = true;
            this._frozenTimerValue = this.engine.timer;
            
            // 1. Center the thread
            this.thread.style.transition = 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
            this.thread.style.left = '50%';
            this.thread.style.transform = 'translateX(-50%)';
            
            // 2. Thread pierces through & Needle moves up
            setTimeout(() => {
                this.thread.style.height = '100%';
                this.needleGroup.style.transition = 'all 0.6s ease-in-out';
                this.needleGroup.style.top = '25%';
                this.needleGroup.style.filter = `drop-shadow(0 0 20px ${this.secondaryColor})`;
            }, 300);

            // 3. Finalize Win (shorter delay, loops removed)
            setTimeout(() => {
                this._isVictoryAnimating = false;
                this.win();
            }, 900);
            
        } else {
            this.lose();
            // Fail Animation
            this.thread.style.transition = 'all 0.4s ease-in';
            this.thread.style.opacity = '0.3';
            this.thread.style.transform = `translateX(${this.direction * 50}px) rotate(${this.direction * 45}deg)`;
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}

